// Cliente Strapi SÚPER SIMPLE
const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337';

// Cache simple para evitar requests duplicados
const cache = new Map();
const CACHE_TTL = 30000; // 30 segundos

function getCacheKey(endpoint) {
  return `${STRAPI_URL}/api${endpoint}`;
}

function isCacheValid(timestamp) {
  return Date.now() - timestamp < CACHE_TTL;
}

// Función para generar slugs localmente
function generateSlug(text) {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .trim();
}

// Marcas locales fijas (10 marcas)
const MARCAS_LOCALES = {
  'mazda': { nombre: 'Mazda', slug: 'mazda' },
  'kia': { nombre: 'Kia', slug: 'kia' },
  'ford': { nombre: 'Ford', slug: 'ford' },
  'lincoln': { nombre: 'Lincoln', slug: 'lincoln' },
  'geely': { nombre: 'Geely', slug: 'geely' },
  'jac': { nombre: 'JAC', slug: 'jac' },
  'shineray': { nombre: 'Shineray', slug: 'shineray' },
  'swm': { nombre: 'SWM', slug: 'swm' },
  'farizon': { nombre: 'Farizon', slug: 'farizon' },
  'forland': { nombre: 'Forland', slug: 'forland' }
};

// Función para determinar marca por modelo (desde Strapi)
function getMarcaInfo(modelo) {
  // Detectar marca basado en patrones del modelo
  if (modelo.includes('CX-') || modelo.includes('Mazda')) {
    return MARCAS_LOCALES.mazda;
  } else if (modelo.includes('Kia') || ['Sonet', 'Sportage', 'Sorento'].some(m => modelo.includes(m))) {
    return MARCAS_LOCALES.kia;
  } else if (modelo.includes('Ford') || ['F-150', 'Explorer', 'Mustang'].some(m => modelo.includes(m))) {
    return MARCAS_LOCALES.ford;
  } else if (modelo.includes('Lincoln')) {
    return MARCAS_LOCALES.lincoln;
  } else if (modelo.includes('Geely')) {
    return MARCAS_LOCALES.geely;
  } else if (modelo.includes('JAC')) {
    return MARCAS_LOCALES.jac;
  } else if (modelo.includes('Shineray')) {
    return MARCAS_LOCALES.shineray;
  } else if (modelo.includes('SWM')) {
    return MARCAS_LOCALES.swm;
  } else if (modelo.includes('Farizon')) {
    return MARCAS_LOCALES.farizon;
  } else if (modelo.includes('Forland')) {
    return MARCAS_LOCALES.forland;
  }
  
  // Por defecto, asumir Kia si no se puede determinar
  return MARCAS_LOCALES.kia;
}

// Función básica para obtener datos con caché
async function fetchStrapi(endpoint) {
  try {
    const cacheKey = getCacheKey(endpoint);
    const cached = cache.get(cacheKey);
    
    // Verificar si tenemos datos en caché válidos
    if (cached && isCacheValid(cached.timestamp)) {
      return cached.data;
    }
    
    const response = await fetch(`${STRAPI_URL}/api${endpoint}`);
    if (!response.ok) return null;
    
    const data = await response.json();
    
    // Guardar en caché
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    return data;
  } catch (error) {
    console.error('Error Strapi:', error);
    return null;
  }
}

// Obtener vehículos destacados (todos por ahora)
export async function getVehiculosDestacados() {
  const data = await fetchStrapi('/vehiculos?populate=*');
  if (!data?.data) return [];
  
  return data.data.map(vehiculo => {
    const marcaInfo = vehiculo.marca ? {
      nombre: vehiculo.marca.nombre,
      slug: vehiculo.marca.slug
    } : getMarcaInfo(vehiculo.modelo);
    
    // Construir URL de imagen principal
    const buildImageUrl = (imageData) => {
      if (!imageData?.url) return null;
      return `${STRAPI_URL}${imageData.url}`;
    };
    
    const imagenPrincipal = buildImageUrl(vehiculo.imagen_principal);
    
    return {
      id: vehiculo.id,
      modelo: vehiculo.modelo,
      año: vehiculo.año,
      precio: vehiculo.precio,
      descripcion: vehiculo.descripcion,
      slug: generateSlug(vehiculo.modelo),
      destacado: vehiculo.destacado,
      marca: marcaInfo.nombre,
      marcaSlug: marcaInfo.slug,
      imagen: imagenPrincipal || `https://d22po4pjz3o32e.cloudfront.net/placeholder-image.svg`,
      categoria: vehiculo.categoria?.nombre || 'SUV'
    };
  });
}

// Obtener todos los vehículos
export async function getVehiculos() {
  const data = await fetchStrapi('/vehiculos?populate=*');
  if (!data?.data) return [];
  
  return data.data.map(vehiculo => {
    const marcaInfo = vehiculo.marca ? {
      nombre: vehiculo.marca.nombre,
      slug: vehiculo.marca.slug
    } : getMarcaInfo(vehiculo.modelo);
    
    // Construir URL de imagen principal
    const buildImageUrl = (imageData) => {
      if (!imageData?.url) return null;
      return `${STRAPI_URL}${imageData.url}`;
    };
    
    const imagenPrincipal = buildImageUrl(vehiculo.imagen_principal);
    
    return {
      id: vehiculo.id,
      modelo: vehiculo.modelo,
      año: vehiculo.año,
      precio: vehiculo.precio,
      descripcion: vehiculo.descripcion,
      slug: generateSlug(vehiculo.modelo),
      destacado: vehiculo.destacado,
      marca: marcaInfo.nombre,
      marcaSlug: marcaInfo.slug,
      imagen: imagenPrincipal || `https://d22po4pjz3o32e.cloudfront.net/placeholder-image.svg`,
      categoria: vehiculo.categoria?.nombre || 'SUV'
    };
  });
}

// Obtener marcas con vehículos
export async function getMarcasConVehiculos() {
  const vehiculos = await getVehiculos();
  
  // Crear estructura de las 10 marcas locales
  const marcas = {};
  Object.entries(MARCAS_LOCALES).forEach(([key, marca]) => {
    marcas[key] = {
      id: key,
      attributes: {
        nombre: marca.nombre,
        slug: marca.slug,
        descripcion: `Vehículos ${marca.nombre}`,
        activa: true,
        orden: Object.keys(MARCAS_LOCALES).indexOf(key) + 1
      },
      vehiculos: []
    };
  });
  
  // Agrupar vehículos de Strapi por marca
  if (vehiculos && vehiculos.length > 0) {
    vehiculos.forEach(vehiculo => {
      const marcaKey = Object.keys(MARCAS_LOCALES).find(key => 
        MARCAS_LOCALES[key].slug === vehiculo.marcaSlug
      );
      
      if (marcaKey && marcas[marcaKey]) {
        marcas[marcaKey].vehiculos.push(vehiculo);
      }
    });
  }
  
  return Object.values(marcas);
}

// Verificar conexión
export async function checkStrapiConnection() {
  const data = await fetchStrapi('/vehiculos?pagination[limit]=1');
  return data !== null;
}

// Transformar marca (súper simple)
export function transformStrapiMarca(marca) {
  return {
    nombre: marca.attributes.nombre,
    slug: marca.attributes.slug,
    descripcion: marca.attributes.descripcion,
    vehiculos: marca.vehiculos || [] // Los vehículos ya vienen procesados con marcaSlug y slug
  };
}

// Obtener detalle de un vehículo específico por marca y modelo
export async function getVehiculoDetalle(marcaSlug, modeloSlug) {
  try {
    // Primero intentar obtener por filtro específico
    const slug = modeloSlug;
    let data = await fetchStrapi(`/vehiculos?filters[modelo][$containsi]=${slug}&populate=*`);
    
    let vehiculoStrapi = null;
    
    if (data?.data?.length > 0) {
      // Buscar coincidencia exacta en los resultados filtrados
      vehiculoStrapi = data.data.find(v => {
        const marcaInfo = getMarcaInfo(v.modelo);
        return marcaInfo.slug === marcaSlug && generateSlug(v.modelo) === modeloSlug;
      });
    }
    
    // Si no encontramos nada, hacer búsqueda completa como fallback
    if (!vehiculoStrapi) {
      data = await fetchStrapi('/vehiculos?populate=*');
      if (!data?.data) return null;
      
      vehiculoStrapi = data.data.find(v => {
        const marcaInfo = getMarcaInfo(v.modelo);
        return marcaInfo.slug === marcaSlug && generateSlug(v.modelo) === modeloSlug;
      });
    }
    
    if (!vehiculoStrapi) return null;
    
    // Construir URLs de imágenes
    const buildImageUrl = (imageData) => {
      if (!imageData?.url) return null;
      return `${STRAPI_URL}${imageData.url}`;
    };
    
    // Mapear datos básicos
    const marcaInfo = getMarcaInfo(vehiculoStrapi.modelo);
    const vehiculoBase = {
      id: vehiculoStrapi.id,
      modelo: vehiculoStrapi.modelo,
      año: vehiculoStrapi.año,
      precio: vehiculoStrapi.precio,
      descripcion: vehiculoStrapi.descripcion,
      slug: generateSlug(vehiculoStrapi.modelo),
      destacado: vehiculoStrapi.destacado,
      marca: marcaInfo.nombre,
      marcaSlug: marcaInfo.slug,
      categoria: vehiculoStrapi.categoria?.nombre || 'SUV',
      imagen: buildImageUrl(vehiculoStrapi.imagen_principal)
    };
    
    // Mapear galería de imágenes
    const galeria = [];
    if (vehiculoStrapi.imagen_principal) {
      galeria.push(buildImageUrl(vehiculoStrapi.imagen_principal));
    }
    if (vehiculoStrapi.galeria_imagenes?.length > 0) {
      vehiculoStrapi.galeria_imagenes.forEach(img => {
        const imageUrl = buildImageUrl(img);
        if (imageUrl) galeria.push(imageUrl);
      });
    }
    
    // Mapear características desde Strapi
    const caracteristicas = [];
    if (vehiculoStrapi.seccion_caracteristicas?.length > 0) {
      vehiculoStrapi.seccion_caracteristicas
        .sort((a, b) => a.orden - b.orden)
        .forEach(carac => {
          caracteristicas.push({
            titulo: carac.titulo,
            valor: carac.descripcion,
            icono: carac.icono
          });
        });
    }
    
    // Mapear especificaciones desde Strapi
    const especificaciones = {};
    if (vehiculoStrapi.seccion_especificaciones) {
      const specs = vehiculoStrapi.seccion_especificaciones;
      especificaciones.motor = specs.motor || 'N/A';
      especificaciones.potencia = specs.potencia || 'N/A';
      especificaciones.torque = specs.torque || 'N/A';
      especificaciones.transmision = specs.transmision || 'N/A';
      especificaciones.traccion = specs.traccion || 'N/A';
      especificaciones.combustible = specs.combustible || 'N/A';
      especificaciones.consumo = specs.consumo || 'N/A';
      especificaciones.tanque = specs.capacidad_tanque || 'N/A';
      especificaciones.peso = specs.peso || 'N/A';
      especificaciones.asientos = specs.asientos || 5;
      especificaciones.autonomia = specs.autonomia || 'N/A';
    }
    
    // Mapear secciones de contenido
    const seccionInnovacion = vehiculoStrapi.seccion_innovacion ? {
      subtitulo: vehiculoStrapi.seccion_innovacion.subtitulo,
      titulo: vehiculoStrapi.seccion_innovacion.titulo,
      descripcion: vehiculoStrapi.seccion_innovacion.descripcion
    } : null;
    
    const seccionPremium = vehiculoStrapi.seccion_premium ? {
      titulo: vehiculoStrapi.seccion_premium.titulo,
      descripcion: vehiculoStrapi.seccion_premium.descripcion
    } : null;
    
    // Expandir con datos completos para la vista de detalle
    return {
      ...vehiculoBase,
      galeria,
      caracteristicas,
      especificaciones,
      seccionInnovacion,
      seccionPremium,
      // Datos adicionales para compatibilidad
      imagen_principal: vehiculoBase.imagen,
      rating: 4.8,
      reviews: 124,
      precio_original: vehiculoBase.precio * 1.15, // Precio "antes" simulado
      badges: [
        vehiculoBase.categoria,
        vehiculoBase.año.toString(),
        especificaciones.combustible || 'Gasolina',
        especificaciones.transmision || 'Automática'
      ]
    };
  } catch (error) {
    console.error('Error fetching vehicle detail from Strapi:', error);
    return null;
  }
} 